/**
 * Newsletter signup: come back to the form after it is submitted.
 *
 * The signup form is a normal Shopify customer form, so submitting it reloads
 * the page. component-newsletter-return-head.liquid (in the head) hides the page
 * on that return trip, so the top of the page never shows. <lp-newsletter-form>
 * wraps the form; when the reloaded page contains the form's success or error
 * message it scrolls the message into view, shows the page, moves focus to the
 * message so screen readers announce it, and removes the customer_posted
 * parameter from the address so a refresh starts clean.
 *
 * Content above the form can still grow while the page finishes loading, so the
 * message is scrolled into view once more after the window load event, unless the
 * shopper has already scrolled themselves.
 */

const MESSAGE_SELECTOR = '.lp-index-newsletter__form-message';
const RETURNING_CLASS = 'lp-newsletter-returning';

export class LpNewsletterForm extends HTMLElement {
  shopperScrolled = false;

  connectedCallback() {
    this.message = this.querySelector(MESSAGE_SELECTOR);

    if (!this.message) {
      this.revealPage();
      return;
    }

    this.message.setAttribute('tabindex', '-1');
    this.scrollToMessage();
    this.revealPage();
    this.message.focus({ preventScroll: true });
    this.removeSignupParameter();

    for (const type of ['wheel', 'touchstart', 'keydown']) {
      window.addEventListener(type, this.onShopperScroll, { once: true, passive: true });
    }
    window.addEventListener('load', this.onLoad, { once: true });
  }

  disconnectedCallback() {
    for (const type of ['wheel', 'touchstart', 'keydown']) {
      window.removeEventListener(type, this.onShopperScroll);
    }
    window.removeEventListener('load', this.onLoad);
  }

  /** Puts the form message in the middle of the screen. */
  scrollToMessage() {
    this.message.scrollIntoView({ block: 'center' });
  }

  /** Shows the page again once it is in position. */
  revealPage() {
    document.documentElement.classList.remove(RETURNING_CLASS);
  }

  /** Drops customer_posted from the address, keeping the rest. */
  removeSignupParameter() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('customer_posted')) return;

    url.searchParams.delete('customer_posted');
    window.history.replaceState({}, '', url);
  }

  /** The shopper took over scrolling, so later layout changes must not move them. */
  onShopperScroll = () => {
    this.shopperScrolled = true;
  };

  /** Content above the form may have grown while loading; put the message back in view. */
  onLoad = () => {
    if (!this.shopperScrolled) this.scrollToMessage();
  };
}

if (!customElements.get('lp-newsletter-form')) {
  customElements.define('lp-newsletter-form', LpNewsletterForm);
}
